<template>
  <card-container
    :id="pageId"
    :class="pageId"
    :disabled="isLoading || !isEmailValid"
    :is-loading="isLoading"
    loading-message="Searching Stores..."
    @reset="resetForm"
    @submit="submitForm"
  >
    <template #title>
      Search available grocery stores by delivery address
    </template>

    <template #content>
      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Grocery Delivery Address*</label>
        <div class="col-sm-7">
          <address-autocomplete-input
            id="groceryAddress"
            address-type="Full Address"
            @place-changed="setAddressData"
          />
        </div>
      </div>
      <div class="row">
        <label class="col-sm-3 control-label pt-10">Email Address</label>
        <div class="col-sm-7">
          <email-input
            v-model="emailAddress"
            @update-validation="updateEmailValidation"
          />
        </div>
      </div>
      <grocery-stores-list :grocery-stores="groceryStores" />
    </template>

    <template #submit-button>
      {{ actionBtnText }}
    </template>
  </card-container>
</template>

<script lang="ts" setup>
import { ref, Ref, computed, defineAsyncComponent } from 'vue';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { LocationDataType, CustomLocationType } from '@/models/address';
import { GroceryStore } from '@/models/grocery';
import { useGrocery } from '@/use/grocery';
import { groceryService } from '@/services/grocery';

const GroceryStoresList = defineAsyncComponent(() => import('@/components/Main/Shared/Grocery/GroceryStoresList.vue') as any);

const emit = defineEmits(['valid']);

const pageId = 'grocery-available-stores';

const { setGroceryAddress } = useGrocery();

const props = defineProps({
  isGroceryRegistration: {
    type: Boolean,
    default: true,
  },
});

const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();

const groceryAddress: Ref<LocationDataType> = ref({
  streetAddress: '',
  state: '',
  city: '',
  zip: '',
});
const emailAddress = ref('');
const groceryStores: Ref<GroceryStore[]> = ref([]);
const isLoading = ref(false);
const isEmailValid = ref(true);

const actionBtnText = computed(() => (emailAddress.value ? 'Send Email' : 'Search'));

const setAddressData = (address: CustomLocationType | null) => {
  groceryAddress.value = {
    streetAddress: address?.streetAddress || '',
    state: address?.state || '',
    city: address?.city || '',
    zip: address?.zip || '',
    country: address?.country || '',
    latitude: address?.latitude ? Number(address.latitude) : null,
    longitude: address?.longitude ? Number(address.longitude) : null,
    googlePlaceId: address?.googlePlaceId,
  };
  if (props.isGroceryRegistration) {
    const data = {
      ...address,
      noteToDriver: '',
      gourmetNoteToDriver: '',
      googlePlaceId: address?.googlePlaceId,
    };
    setGroceryAddress(data);
  }
};

const updateEmailValidation = (value: boolean) => {
  isEmailValid.value = value;
};

const submitForm = async () => {
  isLoading.value = true;
  groceryStores.value = [];

  if (!groceryAddress.value.streetAddress) {
    isLoading.value = false;
    showErrorPopupDialog({
      text: 'You must select the address from the dropdown',
    });
    return;
  }

  try {
    const stores = await groceryService.searchGroceryAvailableStoresAndSendEmail({
      streetAddress: groceryAddress.value.streetAddress!,
      city: groceryAddress.value.city!,
      state: groceryAddress.value.state!,
      zip: groceryAddress.value.zip!,
      latitude: groceryAddress.value.latitude!,
      longitude: groceryAddress.value.longitude!,
      country: groceryAddress.value.country!,
      emailAddress: emailAddress.value,
    });

    if (!stores || !stores.length) {
      showErrorPopupDialog({
        text: 'Grocery service is not available in this area.',
      });
    }

    if (stores && stores.length) {
      emit('valid', true);
    }

    if (stores && stores.length && emailAddress.value) {
      showSuccessPopupDialog({
        text: 'The grocery available store list has been sent successfully.',
      });
    }

    groceryStores.value = stores;
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
    emit('valid', false);
  }
  isLoading.value = false;
};

const resetForm = () => {
  emailAddress.value = '';
  groceryStores.value = [];
};
</script>
